'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { type TrackingGaEvent } from '@/lib/tracking/services/tracking.eventTypes'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Picture } from '@/ui/media/picture/Picture.client'
import type { Image } from '@gql-generated/graphql'
import { Button } from 'react-aria-components'
import { Link } from '../link/Link.client'

export type InlineImageLinkProps = React.PropsWithChildren<
  UiComponentProps<{
    image?: Image
    containerClassName?: string
    overflow?: true
    trackingData?: TrackingGaEvent
    href?: string
    opensInNewTab?: boolean
    onPress?: () => void
    title?: string
  }>
>

export function InlineImageLink({
  children,
  image,
  className,
  containerClassName,
  overflow,
  trackingData,
  href,
  opensInNewTab,
  onPress,
  title,
  id,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: InlineImageLinkProps): React.ReactElement {
  const containerClasses = twMerge(
    'group/inline-image-link flex min-h-5 w-full items-center gap-2 self-stretch py-1 leading-light desktop:px-2',
    containerClassName,
  )

  const content = (
    <>
      {image !== undefined && (
        <div className="flex size-5 shrink-0 items-center justify-center">
          <Picture className="max-h-5 max-w-5" image={image} mode="contain" />
        </div>
      )}
      <div
        className={twMerge(
          className,
          overflow && 'overflow-hidden text-ellipsis whitespace-pre',
          'text-left font-normal group-data-[hovered]/inline-image-link:text-brand-1-800',
        )}
      >
        {children}
      </div>
    </>
  )

  if (href) {
    return (
      <Link
        variant="free"
        underlined="none"
        href={href}
        opensInNewTab={opensInNewTab}
        onPress={onPress}
        title={title}
        trackingData={trackingData}
        className={containerClasses}
        id={id}
        data-testid={testId}
        aria-label={ariaLabel}
      >
        {content}
      </Link>
    )
  }

  // No href: only render an interactive Button when this instance owns its own
  // click behavior, so callers that already wrap it in their own interactive
  // element (e.g. a filter <button>) keep getting a plain, non-nested div
  if (onPress || trackingData) {
    return (
      <Button
        onPress={() => {
          if (trackingData) {
            pushToGtmDataLayer(trackingData)
          }

          onPress?.()
        }}
        preventFocusOnPress
        className={containerClasses}
        id={id}
        data-testid={testId ?? 'inline-image-link'}
        aria-label={ariaLabel}
      >
        {content}
      </Button>
    )
  }

  return (
    <div
      className={containerClasses}
      id={id}
      data-testid={testId ?? 'inline-image-link'}
      aria-label={ariaLabel}
    >
      {content}
    </div>
  )
}
